/**
 * 1.62 — what a part fits (spec docs/superpowers/specs/2026-10-24-fitment-design.md).
 *
 * A product carries `fits` (make · model · optional years · optional note: „Opel · Astra H · 2004–2010 · წინა",
 * „Apple · iPhone 15 Pro") and `oem_codes` (the original's number and the cross numbers). Unlike a barcode (158),
 * an OEM number may sit on any number of products: that sharing is what makes them analogs, with no list to keep.
 *
 * Every rule lives here once — the till, the warehouse list, the exchange dialog, the product card and the Excel
 * import all call it, and migration 187's checks mirror the save rules.
 */
import { codeMatches, type Coded } from './barcodes'

export type Fit = { make: string; model: string; from?: number; to?: number; note?: string }

export const MAX_FITS = 40
export const MAX_OEM = 20
export const MAX_MAKE = 40
export const MAX_MODEL = 60
export const MAX_NOTE = 60
export const MAX_OEM_LEN = 40
export const YEAR_MIN = 1950
export const YEAR_MAX = 2100
/** Shorter than this, a typed number would match half the shelf. */
export const OEM_MIN_QUERY = 4

/** The fields, read tolerantly: a copy saved before 1.62 (or a broken value) has none. */
export function fitsOf(p: { fits?: unknown }): Fit[] {
  return Array.isArray(p.fits) ? (p.fits as Fit[]) : []
}
export function oemOf(p: { oem_codes?: unknown }): string[] {
  return Array.isArray(p.oem_codes) ? (p.oem_codes as string[]).filter((c) => typeof c === 'string') : []
}

/** „93 176-358." and „93176358" are one number. */
export function normOem(code: string): string {
  return code.toUpperCase().replace(/[\s.-]/g, '')
}

/** Two fits are the same entry when these are equal (the card, the import and 187 all use it). */
export function fitKey(f: Fit): string {
  const s = (v: unknown) => String(v ?? '').trim().toLowerCase()
  return `${s(f.make)}|${s(f.model)}|${f.from ?? ''}|${f.to ?? ''}|${s(f.note)}`
}

/** Trimmed, empty note dropped — what a save sends. */
export function cleanFit(f: Fit): Fit {
  const out: Fit = { make: f.make.trim(), model: f.model.trim() }
  if (f.from != null) out.from = f.from
  if (f.to != null) out.to = f.to
  const note = (f.note ?? '').trim()
  if (note) out.note = note
  return out
}

/** The Excel cell is built from these characters, so no field may hold one (Codex's design review #3). */
const FORBIDDEN = /[,;()\r\n]/
/** A year or a range („2012", „2004-2010", „2012-", „-2010") — a note may not look like one. */
export const YEARS_SHAPE = /^(\d{4})?\s*[-–]\s*(\d{4})?$|^\d{4}$/

const yearOk = (y: unknown) => y === undefined || (Number.isInteger(y) && (y as number) >= YEAR_MIN && (y as number) <= YEAR_MAX)

export function fitProblem(f: Fit): 'make' | 'model' | 'years' | 'note' | 'chars' | null {
  const make = (f.make ?? '').trim()
  const model = (f.model ?? '').trim()
  const note = (f.note ?? '').trim()
  if (!make || make.length > MAX_MAKE) return 'make'
  if (!model || model.length > MAX_MODEL) return 'model'
  if (FORBIDDEN.test(make) || FORBIDDEN.test(model) || FORBIDDEN.test(note)) return 'chars'
  if (!yearOk(f.from) || !yearOk(f.to)) return 'years'
  if (f.from !== undefined && f.to !== undefined && f.from > f.to) return 'years'
  if (note.length > MAX_NOTE || (note && YEARS_SHAPE.test(note))) return 'note'
  return null
}

export function oemProblem(code: string): 'empty' | 'long' | 'chars' | null {
  const c = code.trim()
  if (!c || !normOem(c)) return 'empty'
  if (c.length > MAX_OEM_LEN) return 'long'
  if (/[/,;\r\n]/.test(c)) return 'chars'
  return null
}

export function listsProblem(fits: Fit[], codes: string[]): { kind: 'tooManyFits' | 'tooManyCodes' | 'dupFit' | 'dupCode' } | null {
  if (fits.length > MAX_FITS) return { kind: 'tooManyFits' }
  if (codes.length > MAX_OEM) return { kind: 'tooManyCodes' }
  if (new Set(fits.map(fitKey)).size !== fits.length) return { kind: 'dupFit' }
  if (new Set(codes.map(normOem)).size !== codes.length) return { kind: 'dupCode' }
  return null
}

export function yearsText(f: Fit): string {
  if (f.from != null && f.to != null) return f.from === f.to ? `${f.from}` : `${f.from}–${f.to}`
  if (f.from != null) return `${f.from}–`
  if (f.to != null) return `–${f.to}`
  return ''
}

/** The till tile's reason line: model · years · note (the make is left out to fit one line). */
export function fitLabel(f: Fit): string {
  return [f.model, yearsText(f), f.note ?? ''].filter(Boolean).join(' · ')
}

// A till types its product names in Georgian, so the seller may type „ასტრა" for Astra (spec §6.1).
const KA_LATIN: Record<string, string> = {
  'ა': 'a', 'ბ': 'b', 'გ': 'g', 'დ': 'd', 'ე': 'e', 'ვ': 'v', 'ზ': 'z', 'თ': 't', 'ი': 'i', 'კ': 'k', 'ლ': 'l',
  'მ': 'm', 'ნ': 'n', 'ო': 'o', 'პ': 'p', 'ჟ': 'zh', 'რ': 'r', 'ს': 's', 'ტ': 't', 'უ': 'u', 'ფ': 'p', 'ქ': 'k',
  'ღ': 'gh', 'ყ': 'y', 'შ': 'sh', 'ჩ': 'ch', 'ც': 'ts', 'ძ': 'dz', 'წ': 'ts', 'ჭ': 'ch', 'ხ': 'kh', 'ჯ': 'j', 'ჰ': 'h',
}
export function toLatin(s: string): string {
  let out = ''
  for (const ch of s) out += KA_LATIN[ch] ?? ch
  return out
}

export type Reason = { kind: 'oem'; code: string } | { kind: 'fit'; fit: Fit; yearUnknown: boolean }
export type MatchProduct = Coded & { id: string; name: string; category_id: string; fits?: unknown; oem_codes?: unknown }
type Cat = { id: string; name: string; parent_id: string | null }

/** What one product brings to the search, built once per products array (the store never mutates it, 158):
 *  its fits' texts one by one and joined (`all`, a quick „no token here" check), its name lower-cased, its numbers
 *  as typed and normalized (`normAll` joined). */
type Prepared = { fits: Fit[]; texts: string[]; models: string[]; all: string; name: string; codes: string[]; norms: string[]; normAll: string }
/** byId: the products with a fit or a number; at: where each sits in the array; list: them in the array's order (the
 *  search walks these, not 30 000); byNorm: a normalized number → the ids carrying it; fitAll / oemAll: every
 *  product's texts and numbers joined, so a word no part carries skips the walk altogether (spec §6.5, measured). */
type ArrayIndex = {
  byId: Map<string, Prepared>; at: Map<string, number>; list: { i: number; prep: Prepared }[]
  byNorm: Map<string, string[]>; fitAll: string; oemAll: string
}
const arrayIndexes = new WeakMap<readonly object[], ArrayIndex>()

function indexOf(products: readonly MatchProduct[]): ArrayIndex {
  let ix = arrayIndexes.get(products)
  if (!ix) {
    const built: ArrayIndex = { byId: new Map(), at: new Map(), list: [], byNorm: new Map(), fitAll: '', oemAll: '' }
    const fitParts: string[] = []
    const oemParts: string[] = []
    for (let i = 0; i < products.length; i++) {
      const p = products[i]
      const fits = fitsOf(p)
      const codes = oemOf(p)
      if (fits.length === 0 && codes.length === 0) continue
      const norms = codes.map(normOem)
      const texts = fits.map((f) => `${f.make} ${f.model} ${f.note ?? ''}`.toLowerCase())
      const prep: Prepared = { fits, texts, models: fits.map((f) => f.model.toLowerCase()), all: texts.join('\n'), name: p.name.toLowerCase(), codes, norms, normAll: norms.join('\n') }
      built.byId.set(p.id, prep)
      built.at.set(p.id, i)
      built.list.push({ i, prep })
      if (prep.all) fitParts.push(prep.all)
      if (prep.normAll) oemParts.push(prep.normAll)
      for (const n of norms) {
        const ids = built.byNorm.get(n)
        if (!ids) built.byNorm.set(n, [p.id])
        else if (!ids.includes(p.id)) ids.push(p.id)
      }
    }
    built.fitAll = fitParts.join('\n')
    built.oemAll = oemParts.join('\n')
    ix = built
    arrayIndexes.set(products, ix)
  }
  return ix
}

const catIndexes = new WeakMap<readonly Cat[], Map<string, string>>()
/** A category's own name and its parent shelf's, lower-cased — a word like „ხუნდები" finds the shelf. */
function catText(categories: readonly Cat[], id: string): string {
  let m = catIndexes.get(categories)
  if (!m) {
    const byId = new Map(categories.map((c) => [c.id, c]))
    m = new Map()
    for (const c of categories) {
      const parent = c.parent_id ? byId.get(c.parent_id) : undefined
      m.set(c.id, `${c.name} ${parent?.name ?? ''}`.toLowerCase())
    }
    catIndexes.set(categories, m)
  }
  return m.get(id) ?? ''
}

/** `latin[i]` is null when the word is already Latin (one look, not two). */
type Query = { text: string[]; latin: (string | null)[]; year: number | null; yearText: string; oem: string; fitSearch: boolean }

function parseQuery(q: string): Query {
  const tokens = q.trim().toLowerCase().split(/\s+/).filter(Boolean)
  let yearAt = -1
  tokens.forEach((t, i) => {
    if (/^\d{4}$/.test(t) && Number(t) >= YEAR_MIN && Number(t) <= YEAR_MAX) yearAt = i
  })
  const year = yearAt >= 0 ? Number(tokens[yearAt]) : null
  const text = tokens.filter((_, i) => i !== yearAt)
  return {
    text,
    latin: text.map((t) => { const l = toLatin(t); return l === t ? null : l }),
    year,
    yearText: year === null ? '' : String(year),
    oem: normOem(q.trim()),
    // „2012" alone must not list every 2012 car
    fitSearch: text.some((t) => /\p{L}/u.test(t)),
  }
}

const has = (hay: string, q: Query, i: number) => hay.includes(q.text[i]) || (q.latin[i] !== null && hay.includes(q.latin[i]!))

/** The best fit of a product for this query: a certain one beats one whose years are not written. */
function bestFit(prep: Prepared, q: Query, cats: string): { fit: Fit; yearUnknown: boolean } | null {
  // where each word stands outside the fits — the name or the shelf — once per product, not per fit
  const outside: boolean[] = []
  for (let i = 0; i < q.text.length; i++) outside.push(has(prep.name, q, i) || has(cats, q, i))
  let unknown: Fit | null = null
  for (let k = 0; k < prep.fits.length; k++) {
    const own = prep.texts[k]
    let all = true
    let inOwn = false
    for (let i = 0; i < q.text.length; i++) {
      const here = has(own, q, i)
      if (!here && !outside[i]) { all = false; break }
      if (here) inOwn = true
    }
    if (!all || !inOwn) continue
    const f = prep.fits[k]
    if (q.year === null) return { fit: f, yearUnknown: false }
    // a model named like a year (Peugeot 2008) matches as text
    if (prep.models[k].includes(q.yearText)) return { fit: f, yearUnknown: false }
    if (f.from == null && f.to == null) { unknown ??= f; continue }
    if ((f.from ?? -Infinity) <= q.year && q.year <= (f.to ?? Infinity)) return { fit: f, yearUnknown: false }
  }
  return unknown ? { fit: unknown, yearUnknown: true } : null
}

/**
 * The till's search (spec §6). With no fitment or OEM hit the answer is EXACTLY today's filter — same code, same
 * order — so a shop that never typed a fit sees no change (anti-goal 1). Otherwise: OEM hits, certain fits,
 * year-unknown fits, then today's name / code hits not already listed; each group in the products' order.
 */
export function tillMatch<T extends MatchProduct>(
  products: readonly T[],
  categories: readonly Cat[],
  query: string,
): { list: T[]; reasons: Map<string, Reason> } {
  const reasons = new Map<string, Reason>()
  // today's rule, kept to the letter (the grid lower-cased without trimming)
  const lq = query.toLowerCase()
  const plain = (p: T) => !lq || p.name.toLowerCase().includes(lq) || codeMatches(p, lq)
  const q = parseQuery(query)
  const ix = indexOf(products)
  // a number no product carries, or words no fit holds, skip the walk (one look at the joined texts)
  const oemOn = q.oem.length >= OEM_MIN_QUERY && ix.oemAll.includes(q.oem)
  const fitOn = q.fitSearch && q.text.some((_, i) => has(ix.fitAll, q, i))
  if (!oemOn && !fitOn) return { list: lq ? products.filter(plain) : [...products], reasons }

  const oemHits: T[] = []
  const certain: T[] = []
  const unknown: T[] = []
  for (const { i: at, prep } of ix.list) {
    const p = products[at]
    if (oemOn && prep.normAll.includes(q.oem)) {
      const k = prep.norms.findIndex((n) => n.includes(q.oem))
      oemHits.push(p); reasons.set(p.id, { kind: 'oem', code: prep.codes[k] }); continue
    }
    if (!fitOn || prep.fits.length === 0) continue
    // no word of the query in any of its fits: not a fitment hit — the cheap look before the shelf's name
    let any = false
    for (let i = 0; i < q.text.length && !any; i++) any = has(prep.all, q, i)
    if (!any) continue
    const best = bestFit(prep, q, catText(categories, p.category_id))
    if (!best) continue
    ;(best.yearUnknown ? unknown : certain).push(p)
    reasons.set(p.id, { kind: 'fit', fit: best.fit, yearUnknown: best.yearUnknown })
  }
  if (reasons.size === 0) return { list: lq ? products.filter(plain) : [...products], reasons }
  const rest = products.filter((p) => !reasons.has(p.id) && plain(p))
  return { list: [...oemHits, ...certain, ...unknown, ...rest], reasons }
}

/** Products sharing at least one OEM number with `p` — both ways, never itself, in the products' order. */
export function analogsOf<T extends { id: string; oem_codes?: unknown }>(p: T, products: readonly T[]): T[] {
  const norms = oemOf(p).map(normOem)
  if (norms.length === 0) return []
  const ix = indexOf(products as unknown as readonly MatchProduct[])
  const at = new Set<number>()
  for (const n of norms) for (const id of ix.byNorm.get(n) ?? []) if (id !== p.id) at.add(ix.at.get(id)!)
  return [...at].sort((a, b) => a - b).map((i) => products[i])
}

/** The number two products share, in `b`'s own spelling — the picker shows it beside an analog. */
export function sharedCode(a: { oem_codes?: unknown }, b: { oem_codes?: unknown }): string | null {
  const mine = new Set(oemOf(a).map(normOem))
  return oemOf(b).find((c) => mine.has(normOem(c))) ?? null
}

/** One write to `edit_product_fitment` (187): what to add and remove on one product. */
export type FitEdit = { product_id: string; add_fits?: Fit[]; remove_fits?: Fit[]; add_codes?: string[]; remove_codes?: string[] }

/**
 * What a card changed, as additions and removals (Codex's design review #8): applied to the row as the database
 * holds it, so two devices adding fits to one product both keep theirs. Null = nothing changed.
 */
export function fitDiff(
  productId: string,
  loaded: { fits: Fit[]; codes: string[] },
  now: { fits: Fit[]; codes: string[] },
): FitEdit | null {
  const lk = new Set(loaded.fits.map(fitKey))
  const nk = new Set(now.fits.map(fitKey))
  const lc = new Set(loaded.codes.map(normOem))
  const nc = new Set(now.codes.map(normOem))
  const edit = {
    product_id: productId,
    add_fits: now.fits.filter((f) => !lk.has(fitKey(f))),
    remove_fits: loaded.fits.filter((f) => !nk.has(fitKey(f))),
    add_codes: now.codes.filter((c) => !lc.has(normOem(c))),
    remove_codes: loaded.codes.filter((c) => !nc.has(normOem(c))),
  }
  const changed = edit.add_fits.length + edit.remove_fits.length + edit.add_codes.length + edit.remove_codes.length > 0
  return changed ? edit : null
}

/** A call to `edit_product_fitment` carries at most this many rows (187 refuses more; ~one PATCH's worth of time). */
export const FIT_CHUNK = 250

export type FitRefusal = { kind: 'notFound' | 'forbidden' | 'tooMany' | 'invalid' | 'tooManyRows'; productId: string | null }

/** 187's refusals, read from the error message; anything else (a timeout, a network failure) is null. */
export function parseFitRefusal(message: string): FitRefusal | null {
  if (message.includes('FIT_TOO_MANY_ROWS')) return { kind: 'tooManyRows', productId: null }
  if (message.includes('FIT_FORBIDDEN')) return { kind: 'forbidden', productId: null }
  const m = /FIT_(NOT_FOUND|TOO_MANY|INVALID):([0-9a-f-]{36})?/i.exec(message)
  if (!m) return null
  const kind = m[1] === 'NOT_FOUND' ? 'notFound' : m[1] === 'TOO_MANY' ? 'tooMany' : 'invalid'
  return { kind, productId: m[2] ?? null }
}

/** A card row as its five text fields (the years are typed). */
export type FitDraft = { make: string; model: string; from: string; to: string; note: string }

export function draftOf(f: Fit): FitDraft {
  return { make: f.make, model: f.model, from: f.from != null ? String(f.from) : '', to: f.to != null ? String(f.to) : '', note: f.note ?? '' }
}

/** What a row saves as; null for a row left blank. A year that is not a number stays NaN, so fitProblem names it. */
export function fitOfDraft(d: FitDraft): Fit | null {
  const year = (v: string) => (v.trim() === '' ? undefined : /^\d+$/.test(v.trim()) ? Number(v.trim()) : NaN)
  if (!d.make.trim() && !d.model.trim() && !d.from.trim() && !d.to.trim() && !d.note.trim()) return null
  const from = year(d.from)
  const to = year(d.to)
  return cleanFit({ make: d.make, model: d.model, ...(from !== undefined ? { from } : {}), ...(to !== undefined ? { to } : {}), note: d.note })
}

/** The first row a save would refuse (its index), else the lists' own problem; null = it saves. */
export function draftsProblem(
  drafts: FitDraft[],
  codes: string[],
): { row: number | null; kind: NonNullable<ReturnType<typeof fitProblem>> | NonNullable<ReturnType<typeof listsProblem>>['kind'] } | null {
  const fits: Fit[] = []
  for (let i = 0; i < drafts.length; i++) {
    const f = fitOfDraft(drafts[i])
    if (!f) continue
    const bad = fitProblem(f)
    if (bad) return { row: i, kind: bad }
    fits.push(f)
  }
  const lists = listsProblem(fits, codes)
  return lists ? { row: null, kind: lists.kind } : null
}
